import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, Tooltip as MapTooltip, LayersControl, LayerGroup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  FiActivity, FiAlertTriangle, FiMap, FiCheckSquare, FiRefreshCw, FiSearch,
  FiMapPin, FiShield, FiCheckCircle, FiXCircle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import fmdApi from '../services/fmdApi';
import satelliteApi from '../services/satelliteApi';
import './FmdDashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const RISK_COLORS = { Low: '#38a169', Moderate: '#dd6b20', High: '#e53e3e', Severe: '#8b0000' };
const STATUS_COLORS = {
  suspected: '#d69e2e', investigating: '#3182ce', confirmed: '#e53e3e', negative: '#718096', closed: '#a0aec0',
};
const SA_CENTER = [-29.0, 25.0];

const fmtPct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

// Composite-risk factors: weight + plain-language explanation. Mirrors the
// server weights in fmdRiskService (outbreakProximity .40, livestockDensity .25,
// waterAvailability .15, ndviGrazing .10, rainfall .10).
const FACTOR_META = {
  outbreakProximity: { label: 'Outbreak proximity', weight: 0.40, desc: 'Distance-decayed influence of confirmed FMD cases nearby (closer & larger = higher).' },
  livestockDensity: { label: 'Livestock density', weight: 0.25, desc: 'How many herds/reports cluster within ~25 km — more animals, more mixing.' },
  waterAvailability: { label: 'Water availability', weight: 0.15, desc: 'Scarce water forces herds onto shared points (from soil-moisture + rainfall).' },
  ndviGrazing: { label: 'NDVI / grazing', weight: 0.10, desc: 'Sparse vegetation (low NDVI) makes herds roam wider to graze, raising contact.' },
  rainfall: { label: 'Rainfall context', weight: 0.10, desc: 'Drought (congregation) and very wet (virus persistence) both raise risk.' },
};

const FmdDashboard = () => {
  const [activeTab, setActiveTab] = useState('map');
  const [stats, setStats] = useState(null);

  // Map
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedOutbreak, setSelectedOutbreak] = useState(null);

  // Cases
  const [cases, setCases] = useState([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [casesLoading, setCasesLoading] = useState(false);

  // Validation
  const [validation, setValidation] = useState(null);
  const [threshold, setThreshold] = useState(50);
  const [valLoading, setValLoading] = useState(false);

  // Risk lookup by address
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLocation, setLookupLocation] = useState(null);
  const [lookupRisk, setLookupRisk] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const lookupTimer = useRef(null);

  // ---- Loaders ----
  const loadStats = useCallback(async () => {
    try { const d = await fmdApi.getStats(); if (d.success) setStats(d); } catch (e) { /* noop */ }
  }, []);

  const loadMap = useCallback(async () => {
    setMapLoading(true);
    try {
      const d = await fmdApi.getMapLayers();
      if (d.success) setMapData(d);
    } catch (e) { toast.error('Failed to load map layers'); } finally { setMapLoading(false); }
  }, []);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const d = await fmdApi.getCases({ search: caseSearch || undefined, status: statusFilter || undefined, limit: 100 });
      if (d.success) setCases(d.cases);
    } catch (e) { toast.error('Failed to load cases'); } finally { setCasesLoading(false); }
  }, [caseSearch, statusFilter]);

  const loadValidation = useCallback(async (t = threshold) => {
    setValLoading(true);
    try {
      const d = await fmdApi.getValidation(t);
      setValidation(d);
    } catch (e) { toast.error('Failed to load validation metrics'); } finally { setValLoading(false); }
  }, [threshold]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (activeTab === 'map' && !mapData) loadMap(); }, [activeTab, mapData, loadMap]);
  useEffect(() => { if (activeTab === 'cases') loadCases(); }, [activeTab, loadCases]);
  useEffect(() => { if (activeTab === 'validation' && !validation) loadValidation(); }, [activeTab, validation, loadValidation]);

  const openCase = async (caseId) => {
    try {
      const d = await fmdApi.getCaseDetail(caseId);
      if (d.success) setSelectedCase(d.case);
    } catch (e) { toast.error('Failed to load case'); }
  };

  const changeStatus = async (caseId, status) => {
    try {
      const d = await fmdApi.updateCaseStatus(caseId, status);
      if (d.success) {
        toast.success(`Case ${caseId} → ${status}`);
        loadCases(); loadStats();
        setSelectedCase((prev) => (prev && prev.caseId === caseId ? { ...prev, status } : prev));
      }
    } catch (e) { toast.error('Not authorized or update failed'); }
  };

  // ---- Risk lookup by address ----
  const onLookupSearch = (e) => {
    const v = e.target.value;
    setLookupQuery(v);
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (v.length < 2) { setLookupResults([]); return; }
    lookupTimer.current = setTimeout(async () => {
      try {
        const r = await satelliteApi.searchLocations(v);
        setLookupResults(r.success ? r.predictions || [] : []);
      } catch { setLookupResults([]); }
    }, 350);
  };

  const selectLookupPlace = async (prediction) => {
    setLookupResults([]);
    setLookupQuery(prediction.description);
    setLookupLoading(true);
    setLookupRisk(null);
    try {
      const d = await satelliteApi.getPlaceDetails(prediction.placeId || prediction.place_id);
      if (d.success && d.location) {
        const loc = { lat: d.location.lat, lng: d.location.lng, address: prediction.description };
        setLookupLocation(loc);
        const res = await fmdApi.getRiskNearby(loc.lat, loc.lng);
        if (res.success) setLookupRisk(res);
        else { toast.error(res.message || 'Could not compute risk'); }
      } else {
        toast.error('Could not resolve that address');
      }
    } catch (err) {
      toast.error('Lookup failed — please try again');
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => () => { if (lookupTimer.current) clearTimeout(lookupTimer.current); }, []);

  // ---- Seed demo data (button) ----
  const [seeding, setSeeding] = useState(false);
  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await fmdApi.seedDemo();
      if (r.success) {
        toast.success(`Loaded ${r.observations} observations + ${r.demoCases} demo cases`);
        // Refresh everything so the new data shows immediately
        loadStats();
        setMapData(null);
        if (activeTab === 'map') loadMap();
        if (activeTab === 'cases') loadCases();
        loadValidation(threshold);
      } else {
        toast.error(r.message || 'Seeding failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  };

  // ---- Click-anywhere-to-assess-risk on the map ----
  const [clickPoint, setClickPoint] = useState(null);   // [lat, lng]
  const [clickRisk, setClickRisk] = useState(null);      // risk-nearby result
  const [clickAssessing, setClickAssessing] = useState(false);

  const assessPoint = useCallback(async (lat, lng) => {
    setClickPoint([lat, lng]);
    setClickAssessing(true);
    setClickRisk(null);
    setSelectedOutbreak(null);
    try {
      const res = await fmdApi.getRiskNearby(lat, lng);
      if (res.success) setClickRisk(res);
      else toast.error(res.message || 'Could not assess this point');
    } catch {
      toast.error('Could not assess this point');
    } finally {
      setClickAssessing(false);
    }
  }, []);

  // ---- Map circle sizing/coloring ----
  const riskColor = (score) => (score >= 80 ? RISK_COLORS.Severe : score >= 60 ? RISK_COLORS.High : score >= 40 ? RISK_COLORS.Moderate : RISK_COLORS.Low);

  // Leaflet click probe (must live inside <MapContainer>)
  const MapClickProbe = () => {
    useMapEvents({ click: (e) => assessPoint(e.latlng.lat, e.latlng.lng) });
    return null;
  };

  // ---- Validation chart data ----
  const rocChart = validation?.roc ? {
    labels: validation.roc.map((p) => p.fpr),
    datasets: [
      {
        label: 'ROC',
        data: validation.roc.map((p) => ({ x: p.fpr, y: p.tpr })),
        borderColor: '#3182ce', backgroundColor: 'rgba(49,130,206,0.15)',
        fill: true, tension: 0.2, pointRadius: 0,
      },
      {
        label: 'Random', data: [{ x: 0, y: 0 }, { x: 1, y: 1 }],
        borderColor: '#cbd5e0', borderDash: [6, 6], pointRadius: 0, fill: false,
      },
    ],
  } : null;

  const provinceChart = validation?.overlapByProvince ? {
    labels: validation.overlapByProvince.map((p) => p.province),
    datasets: [
      { label: 'DALRRD hotspots', data: validation.overlapByProvince.map((p) => p.dalrrd), backgroundColor: '#e53e3e' },
      { label: 'Model hotspots', data: validation.overlapByProvince.map((p) => p.model), backgroundColor: '#3182ce' },
      { label: 'Overlap', data: validation.overlapByProvince.map((p) => p.overlap), backgroundColor: '#38a169' },
    ],
  } : null;

  const distChart = validation?.riskDistribution ? {
    labels: validation.riskDistribution.map((b) => b.label),
    datasets: [{
      label: 'Cells',
      data: validation.riskDistribution.map((b) => b.count),
      backgroundColor: ['#38a169', '#68d391', '#dd6b20', '#e53e3e', '#8b0000'],
      borderRadius: 6,
    }],
  } : null;

  const WEIGHTS = [
    { label: 'Outbreak proximity', pct: 40, color: '#e53e3e' },
    { label: 'Livestock density', pct: 25, color: '#dd6b20' },
    { label: 'Water availability', pct: 15, color: '#3182ce' },
    { label: 'NDVI / grazing', pct: 10, color: '#38a169' },
    { label: 'Rainfall context', pct: 10, color: '#805ad5' },
  ];

  return (
    <div className="fmd-dashboard">
      {/* Header */}
      <div className="fmd-head">
        <div className="fmd-head-left">
          <h2><FiShield /> FMD Biosecurity Intelligence Layer</h2>
          <span className="fmd-subtitle">Foot-and-Mouth risk model · farmer reports + NDVI grazing-overlap analysis</span>
        </div>
        <div className="fmd-head-right">
          <div className="fmd-head-stats">
            {stats && (
              <>
                <div className="fmd-stat"><b>{stats.activeCount}</b><span>Active cases</span></div>
                <div className="fmd-stat"><b>{stats.byStatus?.confirmed || 0}</b><span>Confirmed</span></div>
                <div className="fmd-stat"><b>{stats.total}</b><span>Total reports</span></div>
              </>
            )}
          </div>
          <div className="fmd-head-actions">
            <a className="fmd-report-link" href="/report" target="_blank" rel="noopener noreferrer">
              <FiMapPin /> Open Farmer Report Page
            </a>
            <button className="fmd-seed-btn" onClick={handleSeed} disabled={seeding}>
              {seeding ? 'Loading…' : 'Load demo data'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="fmd-tabs">
        <button className={activeTab === 'lookup' ? 'active' : ''} onClick={() => setActiveTab('lookup')}><FiMapPin /> Risk Lookup</button>
        <button className={activeTab === 'map' ? 'active' : ''} onClick={() => setActiveTab('map')}><FiMap /> Risk Map</button>
        <button className={activeTab === 'cases' ? 'active' : ''} onClick={() => setActiveTab('cases')}><FiCheckSquare /> Case Queue</button>
        <button className={activeTab === 'validation' ? 'active' : ''} onClick={() => setActiveTab('validation')}><FiActivity /> Validation (TRL-4)</button>
      </div>

      {/* ============ RISK LOOKUP TAB ============ */}
      {activeTab === 'lookup' && (
        <div className="fmd-lookup-tab">
          <div className="fmd-lookup-search-card">
            <h3><FiMapPin /> Enter an address to assess FMD risk</h3>
            <p className="fmd-lookup-sub">Search any location — the model fetches live NDVI, rainfall, livestock density and nearby outbreaks, then computes a composite transmission-risk score.</p>
            <div className="fmd-lookup-search">
              <FiSearch />
              <input
                type="text"
                placeholder="e.g. Mbombela, Mpumalanga or a farm address…"
                value={lookupQuery}
                onChange={onLookupSearch}
              />
              {lookupResults.length > 0 && (
                <ul className="fmd-lookup-results">
                  {lookupResults.map((r, i) => (
                    <li key={r.placeId || i} onClick={() => selectLookupPlace(r)}>
                      <FiMapPin /> <span>{r.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {lookupLoading && (
            <div className="fmd-loading"><div className="fmd-spinner" /> Computing risk at this location…</div>
          )}

          {!lookupLoading && lookupRisk && (
            <div className="fmd-lookup-result">
              <div className="fmd-lookup-headline">
                <div className="fmd-lookup-address">
                  <FiMapPin />
                  <div>
                    <strong>{lookupLocation?.address}</strong>
                    <span>{lookupRisk.location?.province} · {lookupLocation?.lat?.toFixed(3)}, {lookupLocation?.lng?.toFixed(3)}</span>
                  </div>
                </div>
                <div className="fmd-lookup-gauge" style={{ borderColor: riskColor(lookupRisk.risk.score), color: riskColor(lookupRisk.risk.score) }}>
                  <b>{lookupRisk.risk.score}</b><span>/100</span>
                  <em style={{ background: riskColor(lookupRisk.risk.score) }}>{lookupRisk.risk.level}</em>
                </div>
              </div>

              <div className="fmd-lookup-grid">
                <div className="fmd-val-card fmd-calc-card">
                  <h4>How this score was calculated</h4>
                  <p className="fmd-calc-intro">Composite risk = sum of each factor (0–100) × its weight.</p>
                  <table className="fmd-calc-table">
                    <thead><tr><th>Factor</th><th>Score</th><th>Weight</th><th>Points</th></tr></thead>
                    <tbody>
                      {lookupRisk.risk.breakdown && Object.entries(lookupRisk.risk.breakdown).map(([k, v]) => {
                        const meta = FACTOR_META[k] || { label: k, weight: 0 };
                        const points = Math.round(v * meta.weight * 10) / 10;
                        return (
                          <tr key={k}>
                            <td title={meta.desc}>{meta.label}</td>
                            <td>{v}</td>
                            <td>{Math.round(meta.weight * 100)}%</td>
                            <td><b>{points}</b></td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan={3}>Composite risk score</td><td><b>{lookupRisk.risk.score}/100</b></td></tr>
                    </tfoot>
                  </table>
                </div>

                <div className="fmd-val-card">
                  <h4>Grazing & transmission</h4>
                  <ul className="fmd-cd-facts">
                    <li><span>Nearby confirmed outbreaks</span><b>{lookupRisk.risk.nearbyConfirmed ?? 0}</b></li>
                    <li><span>Herds sharing grazing area</span><b>{lookupRisk.risk.grazingOverlap?.overlappingHerds ?? 0}</b></li>
                    <li><span>Likely grazing range</span><b>{lookupRisk.risk.grazingOverlap?.catchmentRadiusKm ?? '—'} km</b></li>
                    <li><span>Shared water point</span><b>{lookupRisk.risk.grazingOverlap?.sharedWater ? 'Yes' : 'No'}</b></li>
                    <li><span>Transmission likelihood</span><b>{lookupRisk.risk.grazingOverlap?.transmissionLikelihood ?? 0}/100</b></li>
                  </ul>
                </div>

                <div className="fmd-val-card">
                  <h4>Earth-observation context</h4>
                  <ul className="fmd-cd-facts">
                    <li><span>NDVI (vegetation)</span><b>{lookupRisk.risk.eoContext?.ndvi ?? 'N/A'}</b></li>
                    <li><span>Grazing pressure</span><b>{lookupRisk.risk.eoContext?.grazingPressure ?? '—'}/100</b></li>
                    <li><span>Water availability</span><b>{lookupRisk.risk.eoContext?.waterAvailability ?? '—'}/100</b></li>
                    <li><span>Recent rainfall</span><b>{lookupRisk.risk.eoContext?.rainfallMm != null ? `${Math.round(lookupRisk.risk.eoContext.rainfallMm)} mm` : '—'}</b></li>
                    <li><span>Livestock density (nearby)</span><b>{lookupRisk.risk.eoContext?.livestockDensity ?? 0}</b></li>
                  </ul>
                  <p className="fmd-lookup-source">NDVI: {lookupRisk.risk.eoContext?.ndviSource || 'Earth observation'}</p>
                </div>
              </div>
            </div>
          )}

          {!lookupLoading && !lookupRisk && (
            <div className="fmd-methodology-card">
              <h4><FiActivity /> How the risk score is calculated</h4>
              <p className="fmd-method-intro">
                For any location the model pulls <strong>live</strong> Earth-observation data (no seed data required)
                and combines five weighted factors into a 0–100 transmission-risk score:
              </p>
              <div className="fmd-method-factors">
                {Object.entries(FACTOR_META).map(([k, m]) => (
                  <div className="fmd-method-factor" key={k}>
                    <div className="fmd-method-factor-head">
                      <span className="fmd-method-weight">{Math.round(m.weight * 100)}%</span>
                      <strong>{m.label}</strong>
                    </div>
                    <p>{m.desc}</p>
                  </div>
                ))}
              </div>
              <p className="fmd-method-note">
                It also models a <strong>grazing/water catchment</strong>: sparse vegetation and scarce water widen how
                far a herd roams, so the system finds neighbouring herds whose catchments overlap and estimates a
                herd-to-herd transmission likelihood. Live inputs: Google Earth Engine NDVI, Open-Meteo rainfall/soil
                moisture, confirmed reports (proximity) and the herd registry (density).
              </p>
              <p className="fmd-method-cta"><FiMapPin /> Search an address above to score a real location.</p>
            </div>
          )}
        </div>
      )}

      {/* ============ MAP TAB ============ */}
      {activeTab === 'map' && (
        <div className="fmd-map-tab">
          <div className="fmd-map-wrap">
            <div className="fmd-map-toolbar">
              <span className="fmd-source"><FiMapPin /> Click anywhere on the map to assess live FMD risk at that point</span>
              <button className="fmd-refresh" onClick={loadMap} disabled={mapLoading}>
                <FiRefreshCw className={mapLoading ? 'spin' : ''} /> {mapLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {mapData && !mapData.outbreaks?.length && !mapData.herds?.length && !mapData.hotspots?.length && (
              <div className="fmd-map-empty-banner">
                No confirmed outbreaks or registered herds yet — the map fills in as farmers submit reports. Use <strong>Load demo data</strong> to preview, or the <strong>Risk Lookup</strong> tab (works with live data, no seeding needed).
              </div>
            )}
            <div className="fmd-map">
              <MapContainer center={SA_CENTER} zoom={6} style={{ height: '100%', width: '100%' }}>
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Street">
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution="&copy; OpenStreetMap &copy; CARTO" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satellite">
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="&copy; Esri" />
                  </LayersControl.BaseLayer>

                  <LayersControl.Overlay checked name="Confirmed / investigating outbreaks">
                    <LayerGroup>
                      {mapData?.outbreaks?.map((o) => (
                        <CircleMarker key={o.caseId} center={[o.lat, o.lng]} radius={9}
                          pathOptions={{ color: riskColor(o.riskScore), fillColor: riskColor(o.riskScore), fillOpacity: 0.8 }}
                          eventHandlers={{ click: () => setSelectedOutbreak(o) }}>
                          <Popup>
                            <strong>{o.caseId}</strong><br />{o.province} · {o.species}<br />
                            Risk {o.riskScore}/100 ({o.status})
                          </Popup>
                        </CircleMarker>
                      ))}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay checked name="Composite risk hotspots">
                    <LayerGroup>
                      {mapData?.hotspots?.map((h, i) => (
                        <CircleMarker key={`h-${i}`} center={[h.lat, h.lng]} radius={Math.max(8, h.score / 4)}
                          pathOptions={{ color: riskColor(h.score), fillColor: riskColor(h.score), fillOpacity: 0.25, weight: 1 }}>
                          <Popup>Risk cell: {h.score}/100 ({h.level})</Popup>
                        </CircleMarker>
                      ))}
                    </LayerGroup>
                  </LayersControl.Overlay>

                  <LayersControl.Overlay name="Livestock density (herds)">
                    <LayerGroup>
                      {mapData?.herds?.map((h, i) => (
                        <CircleMarker key={`herd-${i}`} center={[h.lat, h.lng]} radius={5}
                          pathOptions={{ color: '#805ad5', fillColor: '#805ad5', fillOpacity: 0.5, weight: 1 }}>
                          <Popup>{h.species || 'Herd'} · {h.herdSize || '?'} head<br />Risk {h.risk}/100</Popup>
                        </CircleMarker>
                      ))}
                    </LayerGroup>
                  </LayersControl.Overlay>
                </LayersControl>

                {/* Click-to-assess probe + the assessed point with its grazing catchment */}
                <MapClickProbe />
                {clickPoint && (
                  <>
                    {clickRisk?.risk?.grazingOverlap?.catchmentRadiusKm && (
                      <Circle
                        center={clickPoint}
                        radius={clickRisk.risk.grazingOverlap.catchmentRadiusKm * 1000}
                        pathOptions={{ color: riskColor(clickRisk.risk.score), fillColor: riskColor(clickRisk.risk.score), fillOpacity: 0.08, weight: 1, dashArray: '5 5' }}
                      />
                    )}
                    <CircleMarker
                      center={clickPoint}
                      radius={11}
                      pathOptions={{ color: '#1a3c34', weight: 3, fillColor: clickRisk ? riskColor(clickRisk.risk.score) : '#a0aec0', fillOpacity: 0.9 }}
                    >
                      <MapTooltip permanent direction="top" offset={[0, -10]}>
                        {clickAssessing ? 'Assessing…' : clickRisk ? `Risk ${clickRisk.risk.score}/100 (${clickRisk.risk.level})` : ''}
                      </MapTooltip>
                    </CircleMarker>
                  </>
                )}
              </MapContainer>
            </div>
            <div className="fmd-legend">
              <span><i style={{ background: RISK_COLORS.Severe }} /> Severe (80+)</span>
              <span><i style={{ background: RISK_COLORS.High }} /> High (60–79)</span>
              <span><i style={{ background: RISK_COLORS.Moderate }} /> Moderate (40–59)</span>
              <span><i style={{ background: RISK_COLORS.Low }} /> Low (&lt;40)</span>
            </div>
          </div>

          <div className="fmd-side-panel">
            {/* Point assessment from clicking the map */}
            {(clickPoint || clickAssessing) && (
              <div className="fmd-point-card">
                <h4><FiMapPin /> Point assessment</h4>
                {clickAssessing && <p className="fmd-empty-note">Assessing live risk…</p>}
                {clickRisk && !clickAssessing && (
                  <>
                    <div className="fmd-point-head">
                      <div className="fmd-od-risk" style={{ color: riskColor(clickRisk.risk.score) }}>{clickRisk.risk.score}<small>/100</small></div>
                      <span className="fmd-status-badge" style={{ background: riskColor(clickRisk.risk.score) }}>{clickRisk.risk.level}</span>
                    </div>
                    <ul>
                      <li><span>Province</span><b>{clickRisk.location?.province || '—'}</b></li>
                      <li><span>Coordinates</span><b>{clickPoint[0].toFixed(3)}, {clickPoint[1].toFixed(3)}</b></li>
                      <li title="Vegetation index from Google Earth Engine (Landsat). Lower = sparser grazing.">
                        <span>NDVI (vegetation)</span><b>{clickRisk.risk.eoContext?.ndvi ?? 'N/A'}</b></li>
                      <li title="Confirmed FMD cases within 50 km (distance-weighted).">
                        <span>Nearby outbreaks</span><b>{clickRisk.risk.nearbyConfirmed ?? 0}</b></li>
                      <li title="Estimated daily grazing/watering range — wider when forage/water is scarce.">
                        <span>Grazing range</span><b>{clickRisk.risk.grazingOverlap?.catchmentRadiusKm ?? '—'} km</b></li>
                      <li title="Other herds whose grazing catchment overlaps this point.">
                        <span>Overlapping herds</span><b>{clickRisk.risk.grazingOverlap?.overlappingHerds ?? 0}</b></li>
                    </ul>
                    <button className="fmd-link" onClick={() => setActiveTab('lookup')}>Full breakdown in Risk Lookup →</button>
                  </>
                )}
              </div>
            )}

            <h4>Selected outbreak</h4>
            {selectedOutbreak ? (
              <div className="fmd-outbreak-detail">
                <div className="fmd-od-id">{selectedOutbreak.caseId}</div>
                <span className="fmd-status-badge" style={{ background: STATUS_COLORS[selectedOutbreak.status] }}>{selectedOutbreak.status}</span>
                <div className="fmd-od-risk" style={{ color: riskColor(selectedOutbreak.riskScore) }}>{selectedOutbreak.riskScore}<small>/100</small></div>
                <ul>
                  <li><span>Province</span><b>{selectedOutbreak.province}</b></li>
                  <li><span>Species</span><b>{selectedOutbreak.species}</b></li>
                  <li><span>Affected</span><b>{selectedOutbreak.affectedCount} head</b></li>
                  <li><span>Coordinates</span><b>{selectedOutbreak.lat.toFixed(2)}, {selectedOutbreak.lng.toFixed(2)}</b></li>
                </ul>
                <button className="fmd-link" onClick={() => { setActiveTab('cases'); openCase(selectedOutbreak.caseId); }}>Open full case →</button>
              </div>
            ) : (
              <p className="fmd-empty-note">Click an outbreak marker for case details, or click anywhere on the map to assess that point.</p>
            )}
            <div className="fmd-weights-mini">
              <h4>Composite risk weights</h4>
              {WEIGHTS.map((w) => (
                <div className="fmd-weight-row" key={w.label}>
                  <span>{w.label}</span>
                  <div className="fmd-weight-bar"><div style={{ width: `${w.pct}%`, background: w.color }} /></div>
                  <b>{w.pct}%</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ============ CASES TAB ============ */}
      {activeTab === 'cases' && (
        <div className="fmd-cases-tab">
          <div className="fmd-cases-list">
            <div className="fmd-cases-toolbar">
              <div className="fmd-search">
                <FiSearch />
                <input placeholder="Search case ID, province, species…" value={caseSearch}
                  onChange={(e) => setCaseSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadCases()} />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {['suspected', 'investigating', 'confirmed', 'negative', 'closed'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button className="fmd-refresh" onClick={loadCases} disabled={casesLoading}><FiRefreshCw className={casesLoading ? 'spin' : ''} /></button>
            </div>
            <div className="fmd-table-wrap">
              <table className="fmd-table">
                <thead><tr><th>Case ID</th><th>Location</th><th>Species</th><th>Risk</th><th>Status</th></tr></thead>
                <tbody>
                  {cases.map((c) => (
                    <tr key={c.caseId} className={selectedCase?.caseId === c.caseId ? 'selected' : ''} onClick={() => openCase(c.caseId)}>
                      <td className="fmd-mono">{c.caseId}</td>
                      <td>{c.province}</td>
                      <td>{c.species}</td>
                      <td><span className="fmd-risk-pill" style={{ background: riskColor(c.riskScore) }}>{c.riskScore}/100</span></td>
                      <td><span className="fmd-status-badge" style={{ background: STATUS_COLORS[c.status] }}>{c.status}</span></td>
                    </tr>
                  ))}
                  {!cases.length && !casesLoading && <tr><td colSpan={5} className="fmd-empty-row">No cases match your filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="fmd-case-detail">
            <h4>Case detail</h4>
            {selectedCase ? (
              <>
                <div className="fmd-cd-head">
                  <span className="fmd-mono fmd-cd-id">{selectedCase.caseId}</span>
                  <span className="fmd-status-badge" style={{ background: STATUS_COLORS[selectedCase.status] }}>{selectedCase.status}</span>
                </div>
                <div className="fmd-cd-risk" style={{ color: riskColor(selectedCase.riskScore) }}>
                  {selectedCase.riskScore}<small>/100 · {selectedCase.riskLevel}</small>
                </div>
                <ul className="fmd-cd-facts">
                  <li><span>Affected</span><b>{selectedCase.affectedCount} / {selectedCase.herdSize} head</b></li>
                  <li><span>Province</span><b>{selectedCase.province}</b></li>
                  <li><span>Coordinates</span><b>{selectedCase.coords?.lat.toFixed(2)}, {selectedCase.coords?.lng.toFixed(2)}</b></li>
                  <li><span>Reporter</span><b>{selectedCase.reporter?.name}</b></li>
                </ul>
                <div className="fmd-cd-symptoms">
                  {(selectedCase.symptoms || []).map((s) => <span key={s} className="fmd-symptom-tag">{s.replace(/_/g, ' ')}</span>)}
                </div>

                {selectedCase.riskBreakdown && (
                  <div className="fmd-breakdown">
                    <h5>Risk breakdown</h5>
                    {Object.entries(selectedCase.riskBreakdown).map(([k, v]) => (
                      <div className="fmd-bd-row" key={k}>
                        <span>{k.replace(/([A-Z])/g, ' $1')}</span>
                        <div className="fmd-bd-bar"><div style={{ width: `${v}%` }} /></div>
                        <b>{v}</b>
                      </div>
                    ))}
                  </div>
                )}

                <div className="fmd-sms-preview">
                  <h5>SMS alert preview</h5>
                  <pre>{selectedCase.smsPreview}</pre>
                </div>

                <div className="fmd-status-actions">
                  <span>Update status:</span>
                  <div className="fmd-status-btns">
                    {['investigating', 'confirmed', 'negative', 'closed'].map((s) => (
                      <button key={s} onClick={() => changeStatus(selectedCase.caseId, s)} style={{ borderColor: STATUS_COLORS[s] }}>{s}</button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <p className="fmd-empty-note">Select a case from the queue.</p>
            )}
          </div>
        </div>
      )}

      {/* ============ VALIDATION TAB ============ */}
      {activeTab === 'validation' && (
        <div className="fmd-validation-tab">
          {valLoading && <div className="fmd-loading"><div className="fmd-spinner" /> Computing metrics…</div>}

          {!valLoading && validation?.empty && (
            <div className="fmd-empty-state">
              <FiAlertTriangle size={42} />
              <h3>No validation data</h3>
              <p>{validation.message}</p>
            </div>
          )}

          {!valLoading && validation && !validation.empty && (
            <>
              <div className={`fmd-gate-banner ${validation.decision === 'APPROVE' ? 'approve' : 'hold'}`}>
                <div>
                  <span className="fmd-gate-label">TRL 4 · Validation Package</span>
                  <h3>FMD Risk Model — Validation Dashboard</h3>
                  <p>Held-out evaluation across {validation.n} cell-month observations against confirmed outbreaks. Threshold = {validation.threshold}.</p>
                </div>
                <div className="fmd-gate-decision">
                  Gate decision: <b>{validation.decision}</b>
                </div>
              </div>

              {/* Threshold control */}
              <div className="fmd-threshold">
                <label>Classification threshold: <b>{threshold}</b></label>
                <input type="range" min="10" max="90" value={threshold}
                  onChange={(e) => setThreshold(parseInt(e.target.value, 10))}
                  onMouseUp={() => loadValidation(threshold)} onTouchEnd={() => loadValidation(threshold)} />
                <span className="fmd-threshold-hint">Drag to re-classify — metrics recompute live from stored observations.</span>
              </div>

              {/* Metric cards */}
              <div className="fmd-metric-cards">
                <MetricCard label="Hotspot Overlap" value={fmtPct(validation.metrics.hotspotOverlap)} sub="vs DALRRD (Dice)" />
                <MetricCard label="Sensitivity" value={fmtPct(validation.metrics.sensitivity)} sub="True positive rate" />
                <MetricCard label="Specificity" value={fmtPct(validation.metrics.specificity)} sub="True negative rate" />
                <MetricCard label="Precision" value={fmtPct(validation.metrics.precision)} sub="PPV" />
                <MetricCard label="F1-Score" value={validation.metrics.f1} sub="Harmonic mean" />
                <MetricCard label="AUC-ROC" value={validation.metrics.aucRoc} sub="Discriminative power" />
              </div>

              <div className="fmd-val-grid">
                {/* ROC */}
                <div className="fmd-val-card">
                  <h4>ROC Curve</h4>
                  {rocChart && (
                    <Line data={rocChart} options={{
                      responsive: true, maintainAspectRatio: false,
                      scales: {
                        x: { type: 'linear', min: 0, max: 1, title: { display: true, text: 'False Positive Rate' } },
                        y: { min: 0, max: 1, title: { display: true, text: 'True Positive Rate' } },
                      },
                      plugins: { legend: { display: false } },
                    }} />
                  )}
                  <p className="fmd-auc-note">AUC = {validation.metrics.aucRoc}</p>
                </div>

                {/* Confusion matrix */}
                <div className="fmd-val-card">
                  <h4>Confusion Matrix</h4>
                  <div className="fmd-confusion">
                    <div className="fmd-cm-cell tp"><b>{validation.confusionMatrix.tp}</b><span>True Positives</span></div>
                    <div className="fmd-cm-cell fn"><b>{validation.confusionMatrix.fn}</b><span>False Negatives</span></div>
                    <div className="fmd-cm-cell fp"><b>{validation.confusionMatrix.fp}</b><span>False Positives</span></div>
                    <div className="fmd-cm-cell tn"><b>{validation.confusionMatrix.tn}</b><span>True Negatives</span></div>
                  </div>
                  <div className="fmd-cm-meta">
                    <span>Accuracy <b>{fmtPct(validation.metrics.accuracy)}</b></span>
                    <span>NPV <b>{fmtPct(validation.metrics.npv)}</b></span>
                    <span>n <b>{validation.n}</b></span>
                  </div>
                </div>

                {/* Gate criteria */}
                <div className="fmd-val-card">
                  <h4>TRL 4 Gate Criteria</h4>
                  <table className="fmd-gate-table">
                    <tbody>
                      {validation.gates.map((g) => (
                        <tr key={g.key}>
                          <td>{g.label}</td>
                          <td className="fmd-gate-target">{g.operator} {g.unit === 'ratio' ? `${Math.round(g.target * 100)}%` : g.target}</td>
                          <td className="fmd-gate-value">{g.unit === 'ratio' ? `${Math.round(g.value * 100)}%` : g.value}</td>
                          <td>{g.pass ? <FiCheckCircle className="pass" /> : <FiXCircle className="fail" />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Province overlap */}
                <div className="fmd-val-card wide">
                  <h4>Hotspot Overlap by Province</h4>
                  {provinceChart && (
                    <Bar data={provinceChart} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { position: 'bottom' } },
                      scales: { y: { beginAtZero: true } },
                    }} />
                  )}
                </div>

                {/* Risk distribution */}
                <div className="fmd-val-card wide">
                  <h4>Risk Score Distribution (cells)</h4>
                  {distChart && (
                    <Bar data={distChart} options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true } },
                    }} />
                  )}
                </div>

                {/* Weights */}
                <div className="fmd-val-card">
                  <h4>Composite Risk Weights</h4>
                  {WEIGHTS.map((w) => (
                    <div className="fmd-weight-row" key={w.label}>
                      <span>{w.label}</span>
                      <div className="fmd-weight-bar"><div style={{ width: `${w.pct}%`, background: w.color }} /></div>
                      <b>{w.pct}%</b>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, sub }) => (
  <div className="fmd-metric-card">
    <span className="fmd-mc-label">{label}</span>
    <span className="fmd-mc-value">{value}</span>
    <span className="fmd-mc-sub">{sub}</span>
  </div>
);

export default FmdDashboard;
